/**
 * The versioned track document (Phase 4): the authoritative, portable source
 * of a user-created track. See LEVEL_FORMAT.md for the full schema.
 *
 * Every object type declares its parameters once, in OBJECT_TYPES. The
 * validator, the compiler defaults and the editor inspector all read the same
 * specs, so there is one definition of what a parameter means and allows.
 */
import type { ReactiveBehavior, SurfaceKind, SurfaceVisual } from '../world/SurfaceMaterials';
import type { Vec3Tuple } from '../world/LevelData';

export const TRACK_FORMAT = 'xernous.track';
export const TRACK_FORMAT_VERSION = 1;
/** Bumped whenever compiled geometry for the same document would change. Part of the fingerprint. */
export const COMPILER_VERSION = 1;
/** Movement rulesets a track may declare. Only the Phase 3 baseline exists. */
export const RULESETS = ['xernous.baseline.phase3'] as const;
export type RulesetId = (typeof RULESETS)[number];

/**
 * Resource limits for untrusted documents. Each is far above what the
 * showcase track needs and below what keeps compilation and physics
 * construction interactive (measured in TESTING.md, Phase 4I).
 */
export const LIMITS = Object.freeze({
  /** Serialized size accepted by import, before JSON.parse. */
  maxFileBytes: 2_000_000,
  maxObjects: 2000,
  /** Nesting depth of groups. */
  maxDepth: 8,
  /** Colliders after compilation (tiling and segmentation included). */
  maxSolids: 20_000,
  maxCustomMaterials: 64,
  maxCheckpoints: 64,
  /** |coordinate| of any object or compiled point, metres. f32 spacing at 5 km is ~0.5 mm, below the 1 mm contact skin tolerance. */
  maxCoord: 5000,
  maxIdLength: 64,
  maxNameLength: 120,
  maxDescriptionLength: 4000,
  maxLabelLength: 80,
  /** Editor sketch (planning drawing, never gameplay). */
  maxSketchStrokes: 32,
  maxStrokePoints: 512,
  maxSketchNotes: 64,
  maxNoteLength: 200,
  /** Editor painting (a top-down map drawn in cells; never gameplay). */
  maxPaintCells: 128,
  minPaintCells: 8,
  /** Painted floor heights run 0 … this, in whole metres. */
  maxPaintHeight: 30,
});

export type ParamSpec =
  | { kind: 'number'; label: string; min: number; max: number; default: number; step?: number; unit?: 'm' | 'deg' | '' }
  | { kind: 'int'; label: string; min: number; max: number; default: number }
  | { kind: 'bool'; label: string; default: boolean }
  | { kind: 'enum'; label: string; options: readonly string[]; default: string }
  | { kind: 'text'; label: string; maxLength: number; default: string }
  | { kind: 'color'; label: string; default: number }
  | { kind: 'points'; label: string; minCount: number; maxCount: number; default: readonly Vec3Tuple[] };

export type ParamValue = number | boolean | string | readonly Vec3Tuple[];

export type ObjectCategory = 'group' | 'primitive' | 'movement' | 'gameplay' | 'decor';

export interface ObjectTypeInfo {
  label: string;
  category: ObjectCategory;
  /** Short help shown in the palette. */
  help: string;
  params: Readonly<Record<string, ParamSpec>>;
  /** Uses a surface material (geometry types). */
  material: boolean;
}

const m = (label: string, min: number, max: number, def: number, step = 0.1): ParamSpec => ({ kind: 'number', label, min, max, default: def, step, unit: 'm' });
const deg = (label: string, min: number, max: number, def: number): ParamSpec => ({ kind: 'number', label, min, max, default: def, step: 1, unit: 'deg' });
const segs = (def: number): ParamSpec => ({ kind: 'int', label: 'Segments', min: 1, max: 128, default: def });

export const OBJECT_TYPES = {
  group: { label: 'Group', category: 'group', help: 'Moves its children together.', material: false, params: {} },

  box: { label: 'Box', category: 'primitive', help: 'Solid box, centred on its origin.', material: true, params: { sizeX: m('Size X', 0.05, 400, 4), sizeY: m('Size Y', 0.05, 400, 4), sizeZ: m('Size Z', 0.05, 400, 4) } },
  platform: { label: 'Platform', category: 'primitive', help: 'Flat slab; the origin is the centre of its top surface.', material: true, params: { sizeX: m('Width', 0.2, 400, 8), sizeZ: m('Length', 0.2, 400, 8), thickness: m('Thickness', 0.05, 20, 0.5) } },
  pillar: { label: 'Pillar', category: 'primitive', help: 'Vertical cylinder standing on its origin.', material: true, params: { radius: m('Radius', 0.05, 10, 0.6), height: m('Height', 0.1, 400, 6) } },
  cylinder: { label: 'Cylinder', category: 'primitive', help: 'Cylinder standing on its origin.', material: true, params: { radius: m('Radius', 0.05, 10, 2), height: m('Height', 0.1, 400, 2) } },
  beam: { label: 'Beam', category: 'primitive', help: 'Long box along X, centred on its origin.', material: true, params: { length: m('Length', 0.2, 400, 12), width: m('Width', 0.05, 40, 0.4), height: m('Height', 0.05, 40, 0.4) } },
  wedge: { label: 'Wedge', category: 'primitive', help: 'Triangular prism rising toward −Z; a short kicker.', material: true, params: { width: m('Width', 0.1, 20, 4), height: m('Height', 0.05, 20, 1.5), length: m('Length', 0.1, 20, 4) } },
  ramp: { label: 'Inclined plane', category: 'primitive', help: 'Straight ramp: its top surface starts at the origin and rises toward −Z.', material: true, params: { length: m('Run', 0.5, 400, 16), rise: m('Rise', -200, 200, 4), width: m('Width', 0.2, 100, 6), thickness: m('Thickness', 0.05, 10, 0.5) } },

  curvedRamp: { label: 'Curved ramp', category: 'movement', help: 'Turning ramp starting at the origin heading −Z; positive angles turn left.', material: true, params: { radius: m('Radius', 1, 500, 14), angle: deg('Turn', -360, 360, 90), rise: m('Rise', -200, 200, 4), width: m('Width', 0.5, 60, 6), thickness: m('Thickness', 0.05, 10, 0.5), segments: segs(18) } },
  arc: { label: 'Arc platform', category: 'movement', help: 'Flat curved walkway; a curved ramp without rise.', material: true, params: { radius: m('Radius', 1, 500, 14), angle: deg('Turn', -360, 360, 90), width: m('Width', 0.5, 60, 6), thickness: m('Thickness', 0.05, 10, 0.5), segments: segs(18) } },
  concaveCurve: { label: 'Concave curve', category: 'movement', help: 'Floor curving up toward −Z from its lowest point at the origin.', material: true, params: { radius: m('Radius', 1, 200, 8), angle: deg('Angle', 5, 180, 60), width: m('Width', 0.5, 200, 8), thickness: m('Thickness', 0.05, 10, 0.5), segments: segs(12) } },
  quarterPipe: { label: 'Quarter-pipe', category: 'movement', help: 'A 90° concave curve: floor to vertical wall.', material: true, params: { radius: m('Radius', 1, 200, 6), width: m('Width', 0.5, 200, 10), thickness: m('Thickness', 0.05, 10, 0.5), segments: segs(18) } },
  halfPipe: { label: 'Half-pipe', category: 'movement', help: 'Channel along X with quarter-pipes on both sides of a flat floor.', material: true, params: { radius: m('Radius', 1, 200, 6), length: m('Length', 0.5, 400, 20), flatWidth: m('Floor width', 0, 200, 4), thickness: m('Thickness', 0.05, 10, 0.5), segments: segs(18) } },
  convexCurve: { label: 'Convex curve', category: 'movement', help: 'Crest whose top is at the origin; curves down both ways along Z.', material: true, params: { radius: m('Radius', 1, 500, 12), angle: deg('Angle', 5, 180, 40), width: m('Width', 0.5, 200, 8), thickness: m('Thickness', 0.05, 10, 0.5), segments: segs(8) } },
  curvedWall: { label: 'Curved wall', category: 'movement', help: 'Vertical arc wall around the origin; the concave side faces the origin. 0° is +X, angles turn toward −Z.', material: true, params: { radius: m('Radius', 1, 500, 12), angle: deg('Arc', 5, 360, 90), height: m('Height', 0.2, 200, 5), thickness: m('Thickness', 0.05, 10, 0.6), segments: segs(18) } },
  tunnel: { label: 'Tunnel', category: 'movement', help: 'Closed passage along Z, floor top at the origin.', material: true, params: { profile: { kind: 'enum', label: 'Profile', options: ['square', 'round'], default: 'square' }, length: m('Length', 0.5, 400, 20), width: m('Inner width', 0.8, 60, 4), height: m('Inner height', 1.8, 60, 4), wallThickness: m('Wall', 0.05, 10, 0.5), segments: segs(24) } },
  ring: { label: 'Ring', category: 'movement', help: 'Circular passage (hoop) facing Z; its inner circle sits on the origin.', material: true, params: { radius: m('Inner radius', 0.8, 100, 3), depth: m('Depth', 0.1, 40, 1), thickness: m('Thickness', 0.05, 10, 0.5), segments: segs(40) } },
  corridor: { label: 'Corridor', category: 'movement', help: 'Floor with side walls along Z; floor top at the origin. Narrow it for precision lines.', material: true, params: { length: m('Length', 0.5, 400, 20), width: m('Inner width', 0.8, 60, 3), height: m('Wall height', 0.2, 60, 4), wallThickness: m('Wall', 0.05, 10, 0.5), ceiling: { kind: 'bool', label: 'Ceiling', default: false } } },
  path: { label: 'Path', category: 'movement', help: 'Walkway through control points (local space), smoothed with a centripetal Catmull–Rom spline.', material: true, params: { points: { kind: 'points', label: 'Control points', minCount: 2, maxCount: 64, default: [[0, 0, 0], [0, 0, -12], [-8, 2, -22], [-18, 4, -24]] }, width: m('Width', 0.5, 60, 5), thickness: m('Thickness', 0.05, 10, 0.5), samples: { kind: 'int', label: 'Samples per span', min: 1, max: 32, default: 8 } } },

  spawn: { label: 'Player spawn', category: 'gameplay', help: 'Where a run begins; the yaw sets the facing. Exactly one.', material: false, params: {} },
  start: { label: 'Start zone', category: 'gameplay', help: 'Timer starts when the player leaves it. Axis-aligned; origin at the bottom centre. Exactly one.', material: false, params: { sizeX: m('Size X', 0.5, 200, 4), sizeY: m('Size Y', 0.5, 200, 3), sizeZ: m('Size Z', 0.5, 200, 4) } },
  finish: { label: 'Finish zone', category: 'gameplay', help: 'Entering it finishes the run. Axis-aligned; origin at the bottom centre.', material: false, params: { sizeX: m('Size X', 0.5, 200, 6), sizeY: m('Size Y', 0.5, 200, 4), sizeZ: m('Size Z', 0.5, 200, 1) } },
  checkpoint: { label: 'Checkpoint', category: 'gameplay', help: 'Practice respawn and split. Respawn at the origin facing its yaw. Indices run 0, 1, 2 … in route order.', material: false, params: { index: { kind: 'int', label: 'Order', min: 0, max: 63, default: 0 }, sizeX: m('Size X', 0.5, 200, 6), sizeY: m('Size Y', 0.5, 200, 4), sizeZ: m('Size Z', 0.5, 200, 1) } },
  testSpawn: { label: 'Test spawn', category: 'gameplay', help: 'Editor-only playtest start. Never part of the compiled track; runs from it never set records.', material: false, params: {} },

  light: { label: 'Lantern', category: 'decor', help: 'Decorative point light. Not part of gameplay.', material: false, params: { color: { kind: 'color', label: 'Colour', default: 0xffb46b }, intensity: { kind: 'number', label: 'Intensity', min: 0, max: 200, default: 18, step: 1, unit: '' }, distance: m('Range', 1, 200, 18, 1) } },
  label: { label: 'Label', category: 'decor', help: 'Decorative floating text. Not part of gameplay.', material: false, params: { text: { kind: 'text', label: 'Text', maxLength: LIMITS.maxLabelLength, default: 'Label' } } },
} as const satisfies Record<string, ObjectTypeInfo>;

export type ObjectType = keyof typeof OBJECT_TYPES;
export const OBJECT_TYPE_IDS = Object.keys(OBJECT_TYPES) as ObjectType[];

export interface TrackObject {
  id: string;
  name: string;
  type: ObjectType;
  /** Id of a `group` object, or absent for the root. */
  parent?: string;
  position: Vec3Tuple;
  /** Intrinsic XYZ Euler, degrees. */
  rotationDeg: Vec3Tuple;
  /** Surface material id (geometry types only). */
  material?: string;
  /** Shape parameters; missing keys take the spec default. */
  params: Readonly<Record<string, ParamValue>>;
  /** Editor-only state: never affects gameplay or the fingerprint. */
  hidden?: boolean;
  locked?: boolean;
}

/** A track-defined surface material. Behaviour (kind, behaviors) and appearance (visual) are separate. */
export interface CustomMaterial {
  /** Must start with "custom." so it can never shadow a built-in. */
  id: string;
  name: string;
  kind: Exclude<SurfaceKind, 'trigger'>;
  behaviors: readonly ReactiveBehavior[];
  visual: SurfaceVisual;
}

/** One drawn line of the planned route, in world space, in the order it was drawn. */
export interface SketchStroke {
  id: string;
  points: readonly Vec3Tuple[];
  /** Walkway width the person asked for (m). */
  width: number;
  /** Set once pieces were built along it, so building again skips it. */
  built?: boolean;
}

/** A note pinned in the view ("big jump here", "lava pit"). */
export interface SketchNote {
  id: string;
  position: Vec3Tuple;
  text: string;
}

/**
 * The person's drawing of how the track should go: an editor-only plan that a
 * person or an agent turns into pieces. Never compiled, never in the fingerprint.
 */
export interface TrackSketch {
  strokes: readonly SketchStroke[];
  notes: readonly SketchNote[];
}

/**
 * A top-down painting of the map: one surface kind and one floor height per
 * cell, row by row from −Z (the top of the top view) to +Z, each row from −X to
 * +X, centred on the world origin. Both layers are run-length encoded (see
 * src/editor/paint.ts). An editor-only plan: never compiled, never in the
 * fingerprint; the generator turns it into ordinary objects.
 */
export interface TrackPaint {
  cols: number;
  rows: number;
  /** Metres per cell. */
  cell: number;
  /** Height of painted walls above the floor (m). */
  wallHeight: number;
  kinds: string;
  heights: string;
  /** The person allows a linked agent (MCP) to read and change the painting. */
  allowAgent?: boolean;
}

export interface EditorMeta {
  camera?: { position: Vec3Tuple; target: Vec3Tuple };
  gridStep?: number;
  angleStep?: number;
  sketch?: TrackSketch;
  paint?: TrackPaint;
}

/**
 * The atmosphere a track is drawn in (appearance only: never part of the
 * gameplay fingerprint). 'violet-dusk': open void, violet sky fill.
 * 'dungeon' (Phase 9): enclosed halls lit mainly by their lanterns.
 */
export type EnvironmentPreset = 'violet-dusk' | 'dungeon';
export const ENVIRONMENT_PRESETS: readonly EnvironmentPreset[] = ['violet-dusk', 'dungeon'];

export interface TrackDocument {
  format: typeof TRACK_FORMAT;
  formatVersion: typeof TRACK_FORMAT_VERSION;
  /** Stable identifier; survives renames and edits. */
  id: string;
  title: string;
  description: string;
  author: string;
  ruleset: RulesetId;
  killPlaneY: number;
  environment: { preset: EnvironmentPreset };
  materials: readonly CustomMaterial[];
  objects: readonly TrackObject[];
  editor: EditorMeta;
}

/** Default parameters for a new object of `type`. */
export function defaultParams(type: ObjectType): Record<string, ParamValue> {
  const out: Record<string, ParamValue> = {};
  for (const [k, spec] of Object.entries(OBJECT_TYPES[type].params) as [string, ParamSpec][]) out[k] = spec.default;
  return out;
}

/** A parameter value with the spec default filled in. */
export function param<T extends ParamValue>(obj: TrackObject, key: string): T {
  const v = obj.params[key];
  if (v !== undefined) return v as T;
  const spec = (OBJECT_TYPES[obj.type].params as Record<string, ParamSpec>)[key];
  if (!spec) throw new Error(`object type ${obj.type} has no parameter ${key}`);
  return spec.default as T;
}

export function isGeometryType(type: ObjectType): boolean {
  return OBJECT_TYPES[type].material;
}

export function emptyTrack(id: string, title = 'Untitled track'): TrackDocument {
  return {
    format: TRACK_FORMAT,
    formatVersion: TRACK_FORMAT_VERSION,
    id,
    title,
    description: '',
    author: '',
    ruleset: 'xernous.baseline.phase3',
    killPlaneY: -40,
    environment: { preset: 'violet-dusk' },
    materials: [],
    objects: [],
    editor: { gridStep: 0.5, angleStep: 15 },
  };
}

/** A small playable starting point: floor, spawn, start and finish. */
export function starterTrack(id: string, title = 'New track'): TrackDocument {
  const base = emptyTrack(id, title);
  const obj = (o: Omit<TrackObject, 'rotationDeg' | 'params'> & Partial<Pick<TrackObject, 'rotationDeg' | 'params'>>): TrackObject => ({
    rotationDeg: [0, 0, 0],
    params: defaultParams(o.type),
    ...o,
  });
  return {
    ...base,
    objects: [
      obj({ id: 'floor', name: 'Start floor', type: 'platform', position: [0, 0, -10], material: 'safe.stone', params: { sizeX: 12, sizeZ: 36, thickness: 1 } }),
      obj({ id: 'spawn', name: 'Spawn', type: 'spawn', position: [0, 0.02, 4] }),
      obj({ id: 'start', name: 'Start', type: 'start', position: [0, 0, 4], params: { sizeX: 6, sizeY: 3, sizeZ: 4 } }),
      obj({ id: 'finish', name: 'Finish', type: 'finish', position: [0, 0, -26] }),
    ],
  };
}
